import { useCallback, useEffect, useMemo, useState } from 'react'
import { OptionTypeBase } from 'react-select'
import { FormikHelpers } from 'formik'
import { DropDownItem } from 'types/components'

import { TRANSACTION_TYPES } from 'constants/filters'
import { formatDateToShort } from 'services/helpers'
import {
  MerchantTransactionFilterFormTypes,
  MerchantTransactionFilterFormSubmitValuesTypes,
} from 'types/merchant'
import useMerchantStore from 'store/merchant'
import { getMerchantDevicesAPI } from 'api/merchantdevices'

const FILTERMERCHANTTRANSACTIONINITVAL: MerchantTransactionFilterFormTypes = {
  transactionType: TRANSACTION_TYPES[0],
  startDateRange: new Date(),
  endDateRange: new Date(),
  deviceCode: null,
}

const useTransactionsFilter = ({
  merchantTraderId,
}: {
  merchantTraderId: string
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedMerchant, setSelectedMerchant] =
    useState<OptionTypeBase | null>(null)
  const [merchantId, setMerchantId] = useState<number>()
  const [filterStartDate, setFilterStartDate] = useState<string>()
  const [filterEndDate, setFilterEndDate] = useState<string>()
  const [transactionTypeId, setTransactionTypeId] = useState<number>()
  const [deviceOptions, setDeviceOptions] = useState<DropDownItem[]>([])
  const [initialValues, initialValuesSet] =
    useState<MerchantTransactionFilterFormTypes>({
      ...FILTERMERCHANTTRANSACTIONINITVAL,
    })
  const merchantTransactionLoading = useMerchantStore(
    (state) => state.merchantTransactionLoading
  )
  const merchantTransactionStatus = useMerchantStore(
    (state) => state.merchantTransactionStatus
  )
  const merchantTransaction = useMerchantStore(
    (state) => state.merchantTransaction
  )
  const merchantTransactionBackup = useMerchantStore(
    (state) => state.merchantTransactionBackup
  )
  const getMerchantTransactionFiltered = useMerchantStore(
    (state) => state.getMerchantTransactionFiltered
  )
  const getMerchantTransaction = useMerchantStore(
    (state) => state.getMerchantTransaction
  )

  const isFilteredTransactions = useMemo(() => {
    return merchantTransactionBackup.length ? true : false
  }, [merchantTransactionBackup])
  const transactionsLoading = useMemo(() => {
    return merchantTransactionLoading || false
  }, [merchantTransactionLoading])

  // fetch merchant devices once (used to build device dropdown)
  useEffect(() => {
    let mounted = true
    if (!merchantTraderId) return
    async function loadDevices() {
      try {
        const resp = await getMerchantDevicesAPI({
          traderId: Number(merchantTraderId),
          pSize: 50,
          pNumber: 0,
        })
        if (!mounted) return
        const items = (resp.devices || []).map((d) => ({
          label: String(d.deviceCode),
          value: String(d.deviceId),
        }))
        setDeviceOptions(items)
      } catch (err) {
        setDeviceOptions([])
      }
    }
    loadDevices()
    return () => {
      mounted = false
    }
  }, [merchantTraderId])

  // start: Transaction filter handler
  const handleTransactionFilter = useCallback(
    async (
      values: MerchantTransactionFilterFormSubmitValuesTypes,
      actions: FormikHelpers<MerchantTransactionFilterFormSubmitValuesTypes>
    ) => {
      startDate
        ? setFilterStartDate(formatDateToShort(startDate || new Date()))
        : null
      endDate
        ? setFilterEndDate(formatDateToShort(endDate || new Date()))
        : null
      setTransactionTypeId(parseInt(values.transactionType.value))
      const queryStartDate = startDate
        ? formatDateToShort(startDate || new Date())
        : undefined
      const queryEndDate = endDate
        ? formatDateToShort(endDate || new Date())
        : undefined
      const queryTransactionTypeId =
        values.transactionType.value === '0'
          ? undefined
          : Number(values.transactionType.value)
      const queryDeviceId = values.deviceCode
        ? Number(values.deviceCode)
        : undefined
      await getMerchantTransactionFiltered(Number(merchantTraderId), {
        transactionTypeId: queryTransactionTypeId,
        startDate: queryStartDate,
        endDate: queryEndDate,
        deviceId: queryDeviceId,
      })
      actions.setSubmitting(false)
    },
    [
      selectedMerchant,
      startDate,
      endDate,
      setMerchantId,
      setFilterStartDate,
      setFilterEndDate,
      setTransactionTypeId,
      getMerchantTransactionFiltered,
    ]
  )

  const handleTransactionFilterClear = useCallback(async () => {
    await getMerchantTransaction(Number(merchantTraderId))
  }, [getMerchantTransaction])

  return {
    handleTransactionFilter,
    handleTransactionFilterClear,
    merchantId,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    transactionTypeId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedMerchant,
    setSelectedMerchant,
    initialValues,
    isFilteredTransactions,
    transactionsLoading,
    deviceOptions,
  }
}

export default useTransactionsFilter
